import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";

const statusFlow = ["detected", "contained", "remediated", "reported"] as const;
type Status = typeof statusFlow[number];

const sevColor = (s: string) =>
  s === "critical" ? "bg-destructive text-destructive-foreground"
  : s === "high" ? "bg-destructive/80 text-destructive-foreground"
  : s === "medium" ? "bg-amber-500 text-white"
  : "bg-muted";

export default function ComplianceBreaches() {
  const [breaches, setBreaches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [params] = useSearchParams();
  const { toast } = useToast();
  const [form, setForm] = useState({ portfolio: "", title: "", description: "", severity: "medium" });
  const [scanning, setScanning] = useState(false);

  const runScan = async () => {
    setScanning(true);
    try {
      const { data, error } = await supabase.functions.invoke("sync-compliance-breaches");
      if (error) throw error;
      toast({ title: "Scan complete", description: `Inserted ${data?.inserted ?? 0}, updated ${data?.updated ?? 0}, resolved ${data?.resolved ?? 0}.` });
      load();
    } catch (e: any) {
      toast({ title: "Scan failed", description: e.message ?? String(e), variant: "destructive" });
    } finally { setScanning(false); }
  };

  const load = async () => {
    setLoading(true);
    let q = supabase.from("compliance_breaches").select("*").order("detected_at", { ascending: false });
    if (params.get("portfolio")) q = q.eq("portfolio", params.get("portfolio")!);
    if (params.get("source")) q = q.eq("source", params.get("source") as any);
    const { data } = await q;
    setBreaches(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [params]);

  const advance = async (id: string, current: Status) => {
    const idx = statusFlow.indexOf(current);
    if (idx < 0 || idx === statusFlow.length - 1) return;
    const next = statusFlow[idx + 1];
    const stamp: any = { status: next };
    if (next === "contained") stamp.contained_at = new Date().toISOString();
    if (next === "remediated") stamp.remediated_at = new Date().toISOString();
    if (next === "reported") stamp.reported_at = new Date().toISOString();
    const { error } = await supabase.from("compliance_breaches").update(stamp).eq("id", id);
    if (error) toast({ title: "Update failed", description: error.message, variant: "destructive" });
    else load();
  };

  const create = async () => {
    const { data: u } = await supabase.auth.getUser();
    const { error } = await supabase.from("compliance_breaches").insert({
      source: "manual",
      portfolio: form.portfolio || null,
      title: form.title,
      description: form.description,
      severity: form.severity as any,
      created_by: u.user?.id,
    });
    if (error) toast({ title: "Create failed", description: error.message, variant: "destructive" });
    else { setOpen(false); setForm({ portfolio: "", title: "", description: "", severity: "medium" }); load(); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Breach Register (§16)</CardTitle>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={runScan} disabled={scanning}>
            {scanning ? "Scanning…" : "Run policy scan"}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm">Log breach</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Manual breach entry</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Portfolio</Label><Input value={form.portfolio} onChange={(e) => setForm({ ...form, portfolio: e.target.value })} placeholder="P3, R2, etc." /></div>
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div><Label>Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["low","medium","high","critical"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={create} disabled={!form.title}>Save</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? <p className="text-muted-foreground text-sm">Loading…</p> :
          breaches.length === 0 ? <p className="text-muted-foreground text-sm">No breaches recorded.</p> :
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="border-b">
                <tr>
                  <th className="text-left p-2">Detected</th>
                  <th className="text-left p-2">Portfolio</th>
                  <th className="text-left p-2">Source</th>
                  <th className="text-left p-2">Title</th>
                  <th className="text-left p-2">Severity</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {breaches.map((b) => (
                  <tr key={b.id} className="border-b">
                    <td className="p-2">{format(new Date(b.detected_at), "dd/MM/yyyy")}</td>
                    <td className="p-2">{b.portfolio ?? "—"}</td>
                    <td className="p-2 text-xs">{b.source}</td>
                    <td className="p-2"><div className="font-medium">{b.title}</div><div className="text-xs text-muted-foreground">{b.description}</div></td>
                    <td className="p-2"><Badge className={sevColor(b.severity)}>{b.severity}</Badge></td>
                    <td className="p-2"><Badge variant="outline">{b.status}</Badge></td>
                    <td className="p-2">
                      {b.status !== "reported" && (
                        <Button size="sm" variant="ghost" onClick={() => advance(b.id, b.status)}>
                          → {statusFlow[statusFlow.indexOf(b.status) + 1]}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </CardContent>
    </Card>
  );
}
